import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import isUUID from '~/utils/isUUID';

type RoomAmenity = string | { name?: string | null };

export interface SaveOfferBuilderRoomParams {
  offerId: string;
  propertyId: string;
  room: {
    id?: string;
    amenities?: Array<RoomAmenity>;
    availability_threshold?: string | null;
    bed_groups?: Array<{
      id?: string;
      configuration?: Array<{ type?: string; size?: string; quantity?: number }>;
      description?: string | null;
    }>;
    category?: string | null;
    description?: string;
    has_live_cm_data?: boolean | null;
    name?: string;
    number_of_bathrooms?: number | null;
    number_of_bedrooms?: number | null;
    number_of_beds?: Array<number> | number;
    property_id?: string;
    room_policies?: string | null;
    room_type_code?: string | null;
    size_sqm?: number | null;
    source?: 'admin' | 'cm-sync' | null;
  };
  roomPolicyId?: number | null;
}

type SavedRoomType = SaveOfferBuilderRoomParams['room'] & { id: string };

export type SavedOfferBuilderRoom = SavedRoomType & {
  roomPolicyId: number | null;
};

export function useSaveOfferBuilderRoom() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ offerId, propertyId, room, roomPolicyId }: SaveOfferBuilderRoomParams) => {
      const roomPayload = buildRoomTypePayload(room, propertyId);
      const response = room.id
        ? await ReservationService.updateRoomType(roomPayload, propertyId, room.id)
        : await ReservationService.createRoomType(roomPayload, propertyId);
      const savedRoom = response.result as SavedRoomType;
      let savedRoomPolicyId = roomPolicyId ?? null;

      const roomPolicyDescription = room.room_policies ?? '';
      const shouldSaveRoomPolicy = hasRoomPolicyToSave(room, roomPolicyId);

      if (shouldSaveRoomPolicy && savedRoom.id) {
        try {
          if (roomPolicyId) {
            await OffersService.updateRoomPolicy({ description: roomPolicyDescription }, offerId, roomPolicyId);
          } else {
            const roomPolicyResponse = await OffersService.createRoomPolicy(
              {
                description: roomPolicyDescription,
                room_type_id: savedRoom.id,
              },
              offerId,
            );
            savedRoomPolicyId = getRoomPolicyIdFromResponse(roomPolicyResponse);
          }
        } catch (error) {
          enqueueSnackbar(`Room saved but policy failed: ${getErrorMessage(error)}`, { variant: 'warning' });
        }
      }

      return {
        ...savedRoom,
        room_policies: room.room_policies ?? savedRoom.room_policies,
        roomPolicyId: savedRoomPolicyId,
      };
    },
    onSuccess: (savedRoom) => {
      enqueueSnackbar(`${savedRoom.name || 'Room'} saved`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to save room: ${error.message}`, { variant: 'error' });
    },
  });
}

function hasRoomPolicyToSave(room: SaveOfferBuilderRoomParams['room'], roomPolicyId?: number | null) {
  return (
    room.room_policies !== undefined && room.room_policies !== null && (!!roomPolicyId || room.room_policies !== '')
  );
}

function buildRoomTypePayload(room: SaveOfferBuilderRoomParams['room'], fallbackPropertyId: string) {
  return {
    amenities: sanitiseAmenities(room.amenities ?? []),
    availability_threshold: room.availability_threshold ?? null,
    bed_groups: sanitiseBedGroups(room.bed_groups ?? [], !!room.id),
    category: room.category ?? null,
    description: room.description ?? '',
    has_live_cm_data: room.has_live_cm_data ?? null,
    name: room.name ?? '',
    number_of_bathrooms: room.number_of_bathrooms ?? null,
    number_of_bedrooms: room.number_of_bedrooms ?? null,
    number_of_beds: room.number_of_beds ?? [],
    property_id: room.property_id ?? fallbackPropertyId,
    room_type_code: room.room_type_code ?? null,
    size_sqm: room.size_sqm ?? null,
    source: room.source ?? 'admin',
  };
}

function sanitiseAmenities(amenities: Array<RoomAmenity>) {
  return amenities
    .map((amenity) => (typeof amenity === 'string' ? amenity : amenity.name))
    .filter((amenityName): amenityName is string => !!amenityName);
}

function sanitiseBedGroups(
  roomBedGroups: NonNullable<SaveOfferBuilderRoomParams['room']['bed_groups']>,
  includePersistedIds: boolean,
) {
  return roomBedGroups.map((bedGroup) => ({
    ...(includePersistedIds && isPersistedBedGroupId(bedGroup.id) ? { id: bedGroup.id } : {}),
    configuration: (bedGroup.configuration ?? [])
      .map((bedConfiguration) => ({
        quantity: bedConfiguration.quantity ?? 0,
        type: bedConfiguration.type ?? '',
      }))
      .filter((bedConfiguration) => bedConfiguration.type),
  }));
}

function isPersistedBedGroupId(id?: string) {
  return isUUID(id);
}

function getRoomPolicyIdFromResponse(response: unknown) {
  const result = (response as { result?: { id?: unknown; room_policy_id?: unknown; roomPolicyId?: unknown } })?.result;
  const roomPolicyId = result?.id ?? result?.room_policy_id ?? result?.roomPolicyId;

  return typeof roomPolicyId === 'number' ? roomPolicyId : null;
}
